<template>
  <v-dialog v-model="open" scrollable persistent max-width="360px">
    <v-card class="c-edit-event">
      <!-- Header -->
      <v-card-title>
        {{ $T('page.cal.repeat.btn') }}
      </v-card-title>

      <v-divider></v-divider>
      <v-card-text>
        <v-flex v-if="!data.type || data.type == 'none'">
          <div class="c-type-radio-ttl">{{ $T('page.cal.repeat.type') }}</div>
          <v-radio-group class="c-type-radio" v-model="data.type" color="clr-primary" :mandatory="false">
            <v-radio
              v-for="type in types"
              :key="type.value"
              color="clr-primary"
              :label="type.label"
              :value="type.value"
            ></v-radio>
          </v-radio-group>
        </v-flex>
        <v-flex v-else>
          <v-select
            :items="types"
            :label="$T('page.cal.repeat.type')"
            v-model="data.type"
            item-text="label"
            item-value="value"
          ></v-select>
        </v-flex>
        <v-flex v-if="data.type == 'dates'" class="c-dates c-flex jc">
          <v-date-picker
            v-model="dates"
            color="clr-primary"
            :locale="userLang"
            :day-format="date => new Date(date).getDate()"
            multiple
            no-title
            scrollable
          >
          </v-date-picker>
        </v-flex>
        <v-flex v-if="data.type == 'week'">
          <table class="w100">
            <tr>
              <td v-for="i in 7" class="c-week-td">
                <div class="c-week-t">{{ weekdayKey(i - 1) }}</div>
              </td>
            </tr>
            <tr>
              <td v-for="i in 7">
                <div class="c-week-c">
                  <v-checkbox
                    v-model="weeks"
                    color="clr-primary"
                    :value="`w${i - 1}`"
                    :disabled="weekday == `w${i - 1}`"
                  ></v-checkbox>
                </div>
              </td>
            </tr>
          </table>
        </v-flex>
        <v-flex v-if="data.type != 'none'">
          <div class="c-summary">{{ repeatMsg }}</div>
        </v-flex>
        <v-flex class="c-range" v-if="['week', 'month', 'year'].indexOf(data.type) >= 0">
          <datetime-picker
            class="c-start"
            v-model="data.start"
            ref="start"
            :dateOnly="true"
            :dateLabel="$T('event.start_date')"
            :timeLabel="''"
            :maxDate="startDate"
          ></datetime-picker>
          <datetime-picker
            class="c-end"
            v-model="data.end"
            ref="end"
            :dateOnly="true"
            :dateLabel="$T('event.end_date')"
            :timeLabel="''"
            :minDate="startDate"
            :errMsg="endDateError"
            :pickerDate="data.start"
          ></datetime-picker>
          <v-checkbox
            class="c-endless"
            v-model="endless"
            :label="$T('page.cal.repeat.endless')"
            color="clr-primary"
          ></v-checkbox>
        </v-flex>
      </v-card-text>
      <v-divider></v-divider>

      <!-- Footer -->
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="clr-btn" flat @click.native="onOk">{{ $T('button.ok') }}</v-btn>
        <v-btn color="clr-btn-cancel" flat @click.native="onCancel">{{ $T('button.cancel') }}</v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import datetimePicker from '../common/DateTimePicker.vue';
import { COMMIN } from '../../constant';

export default {
  components: {
    'datetime-picker': datetimePicker,
  },
  props: {
    value: { type: Object, required: false },
    start: { type: String, required: true },
    end: { type: String, required: true },
    allDay: { type: Boolean, required: true },
    hasRepeatFromEditStart: { type: Boolean, required: true },
  },
  data() {
    return {
      data: {
        // none, dates, week, month, year
        editted: false,
        rangeSet: false,
        type: 'none',
        // YYYY-MM-DD, ...
        dates: [],
        // w0, w1... w0=sunday
        weeks: [],
        adjustTail: false,
        start: null,
        // YYYY-MM-DD
        end: null,
      },
      endless: true,
      // multicheckbox does not work inside of object...
      weeks: [],
      // multicheckbox does not work inside of object...
      dates: [],
      types: [
        { label: $T('param.none'), value: 'none' },
        { label: $T('page.cal.repeat.days'), value: 'dates' },
        { label: $T('common.unit.every.week'), value: 'week' },
        { label: $T('common.unit.every.month'), value: 'month' },
        { label: $T('common.unit.every.year'), value: 'year' },
      ],
      datesMenu: false,
      open: false,
      endDateError: null,
      typeSelected: false,
    };
  },
  mounted() {
    this.resetValue();
  },
  computed: {
    isEdit() {
      return !!this.event;
    },
    repeatMsg() {
      var type = this.data.type;
      if (type == 'none' || !type) return '';
      if (type == 'dates') return $T('page.cal.repeat.every.dates', { range: this.toDateString });
      if (type == 'week') return $T('page.cal.repeat.every.week', { range: this.toDateString });
      if (type == 'month') return $T('page.cal.repeat.every.month', { d: this.toMonthString });
      if (type == 'year') return $T('page.cal.repeat.every.year', { md: this.toYearString });
      console.error(`unknown type ${type}`);
      return '';
    },
    toDateString() {
      var from = this.$moment(this.start).format('HH:mm');
      if (this.isSameDate) {
        if (this.allDay) return $T('common.all_day');
        return `${from} - ${this.$moment(this.end).format('HH:mm')}`;
      }
      return this.toDiffString(from);
    },
    toMonthString() {
      var from = this.$moment(this.start).format(this.allDay ? 'Do' : 'Do HH:mm');
      if (this.isSameDate) {
        if (this.allDay) return from;
        return `${from} - ${this.$moment(this.end).format('HH:mm')}`;
      }
      return this.toDiffString(from);
    },
    toYearString() {
      var fmt = this.userLang == 'en' && this.userCountry == 'us' ? 'Do MMM' : 'MMM Do';
      if (!this.allDay) fmt = fmt + ' HH:mm';
      var from = this.$moment(this.start).format(fmt);
      if (this.isSameDate) {
        if (this.allDay) return from;
        return `${from} - ${this.$moment(this.end).format('HH:mm')}`;
      }
      return this.toDiffString(from);
    },
    isSameDate() {
      return this.start.substring(0, 10) == this.end.substring(0, 10);
    },
    weekday() {
      return 'w' + this.$moment(this.start).weekday();
    },
    startDate() {
      return this.start.substring(0, 10);
    },
  },
  watch: {
    open(n, o) {
      if (!n) {
        this.$emit('close');
      }
    },
    endless: function(n, o) {
      if (n) this.data.end = null;
      else if (this.$refs.end) this.$refs.end.focus();
    },
    'data.end': function(n, o) {
      if (n) {
        this.endDateError = null;
        this.endless = false;
      }
    },
    'data.type': function(n, o) {
      if (n != 'none' && !this.data.start) {
        this.data.start = this.start;
      }
    },
  },
  methods: {
    resetValue() {
      this.data = Object.assign({}, this.value);
      this.endless = !this.data.end;
    },
    openDialog(initWeeks, initDates) {
      this.resetValue();
      this.weeks = initWeeks;
      this.data.weeks = this.weeks;
      this.dates = initDates;
      this.data.dates = this.dates;
      this.open = true;
    },
    onOk() {
      if (!this.endless && !this.data.end) {
        this.endDateError = $T('validation.required', { attribute: $T('event.end_date') });
        return;
      }
      this.data.weeks = this.weeks.filter(n => n != this.weekday);

      var t = this.start.substring(0, 10);
      this.data.dates = this.dates.filter(n => n != t);

      if (['none', 'dates'].indexOf(this.data.type) == -1) {
        this.data.rangeSet = true;
      }
      if (this.data.start && this.data.start.length > 10) this.data.start = this.data.start.substring(0, 10);
      if (this.data.end && this.data.end.length > 10) this.data.end = this.data.end.substring(0, 10);
      this.$emit('input', this.data);
      this.open = false;
    },
    onCancel() {
      this.open = false;
    },
    weekdayKey(idx) {
      var m = this.$moment().startOf('week');
      if (idx > 0) {
        m = m.add(idx, 'day');
      }
      return m.format('ddd');
    },
    toDiffString(from) {
      var s = this.$moment(this.start.substring(0, 10));
      var e = this.$moment(this.end.substring(0, 10));
      var duration = this.$moment.duration(e.diff(s));
      var days = duration.asDays();
      if (this.allDay) return $T('page.cal.repeat.diff.d', { from: from, d: days });
      return $T('page.cal.repeat.diff.dhm', { from: from, d: days, hm: this.$moment(this.end).format('HH:mm') });
    },
  },
};
</script>

<style scoped>
.c-week-td {
  position: relative;
}
.c-week-t {
  position: absolute;
  left: 12px;
  top: 0;
  transform: translateX(-50%);
}
.c-week-c {
  margin-bottom: -20px;
}
.c-range {
  margin-top: 20px;
}
.c-end {
  margin-top: -10px;
}
.c-endless {
  margin: -5px 0 -20px 0px;
}
.c-summary {
  font-size: 16px;
}
.c-dates {
  margin-bottom: 15px;
}

.c-type-radio {
  margin: 5px 5px -20px 15px;
}
.c-type-radio-ttl {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.54);
  margin: 5px 0 0 10px;
}
</style>
